import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, Button, Loader } from "@/components/ui/Primitives";
import { toast } from "sonner";
import {
  Wrench, Network, MessageSquare, Receipt,
  Camera, TrendingUp, AlertTriangle, BarChart3,
  Sparkles, ArrowRight, CheckCircle2, AlertCircle,
  DollarSign, KeyRound, Megaphone,
} from "lucide-react";

/* ---------------------------------------------------------------------------
   Smart features hub — single page, 8 feature cards, every one calls a
   Claude-powered backend endpoint under /api/smart/*. Each card renders
   an input form + a Run button + a result panel that shows the structured
   JSON nicely formatted.

   We deliberately keep ONE component per feature (small, self-contained) and
   share a SmartCard wrapper so they look consistent.
--------------------------------------------------------------------------- */

const FEATURES = [
  { id: "maintenance", icon: Wrench,        title: "Smart Maintenance Triage",       blurb: "24/7 issue triage with auto-dispatch recommendation." },
  { id: "agents",      icon: Network,       title: "Smart Multi-Agent Orchestration", blurb: "Dozens of agents coordinate leasing, maintenance and unit prep." },
  { id: "ledger",      icon: MessageSquare, title: "Smart Conversational Ledger",     blurb: "Ask plain-English questions of your financial ledger." },
  { id: "books",       icon: Receipt,       title: "Smart Bookkeeping & Taxes",       blurb: "CRA-aware Q&A — T776, NR4, CCA classes, principal-residence exemption." },
  { id: "visual",      icon: Camera,        title: "Smart Visual Condition Assessment", blurb: "Smart vision on tenant photos: condition score, issues, costs." },
  { id: "retention",   icon: TrendingUp,    title: "Smart Move-Out & Renewal Risk",   blurb: "Calibrated retention-risk score per tenant + recommended actions." },
  { id: "anomaly",     icon: AlertTriangle, title: "Smart Anomaly & Expense Detection", blurb: "Duplicate invoices, utility spikes, vendor overcharges — flagged." },
  { id: "benchmark",   icon: BarChart3,     title: "Smart Portfolio Benchmarking",    blurb: "Your KPIs vs. local CMHC / CRA / Altus benchmarks." },
  { id: "brokerage",   icon: DollarSign,    title: "Smart Brokerage Split Payments",   blurb: "Native 90/10 owner-brokerage routing at the moment of rent. No trust account." },
  { id: "locks",       icon: KeyRound,      title: "Smart Lock Vendor Verification",   blurb: "Time-restricted PINs that expire the second a work order closes." },
  { id: "leasing",     icon: Megaphone,     title: "Smart Leasing Automation",         blurb: "One listing → Kijiji, FB Page, FB Marketplace, Zumper, Rentals.ca." },
];

function SmartCard({ id, icon: Icon, title, blurb, active, onClick }) {
  return (
    <button
      type="button"
      data-testid={`smart-tab-${id}`}
      onClick={onClick}
      className={`text-left p-5 rounded-2xl border transition-all w-full
        ${active
          ? "border-emerald-600 bg-emerald-50/60 ring-2 ring-emerald-200"
          : "border-slate-200 bg-white hover:border-emerald-300 hover:shadow-sm"}`}
    >
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3
        ${active ? "bg-emerald-600 text-white" : "bg-emerald-50 text-emerald-700"}`}>
        <Icon className="w-5 h-5"/>
      </div>
      <h3 className="font-display text-base font-bold text-slate-900 leading-tight">{title}</h3>
      <p className="mt-1.5 text-xs text-slate-600 leading-relaxed">{blurb}</p>
    </button>
  );
}

function ResultPanel({ result, error, latencyMs }) {
  if (error) {
    return (
      <div className="mt-4 p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-800 flex gap-2" data-testid="smart-result-error">
        <AlertCircle className="w-4 h-4 mt-0.5 flex-none"/>
        <div><b>Could not run:</b> {error}</div>
      </div>
    );
  }
  if (!result) return null;
  return (
    <div className="mt-5 space-y-3" data-testid="smart-result">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700">
        <CheckCircle2 className="w-3.5 h-3.5"/> Smart feature output
        {latencyMs && <span className="ml-auto text-slate-400 normal-case tracking-normal font-normal">{latencyMs}ms</span>}
      </div>
      <pre className="text-xs bg-slate-50 border border-slate-200 rounded-xl p-4 overflow-x-auto leading-relaxed">
{JSON.stringify(result, null, 2)}
      </pre>
    </div>
  );
}

/* Generic field components — keep forms compact + accessible */
const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-[0.14em] font-bold text-slate-500">{label}</span>
    <div className="mt-1.5">{children}</div>
    {hint && <p className="mt-1 text-[11px] text-slate-400">{hint}</p>}
  </label>
);
const Input = (props) => (
  <input {...props}
    className={`w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-sm ${props.className || ""}`}/>
);
const Textarea = (props) => (
  <textarea rows={3} {...props}
    className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-sm leading-relaxed resize-y"/>
);
const Select = ({ children, ...rest }) => (
  <select {...rest}
    className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-sm bg-white">
    {children}
  </select>
);

/* -------------------- Feature: Maintenance Triage -------------------- */
function MaintenanceTriage() {
  const [form, setForm] = useState({
    description: "",
    property_label: "",
    tenant_name: "",
    reported_via: "web",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [latency, setLatency] = useState(null);
  const [error, setError] = useState(null);
  const run = async () => {
    setLoading(true); setResult(null); setError(null); setLatency(null);
    try {
      const { data } = await api.post("/smart/maintenance/triage", form);
      setResult(data.data); setLatency(data.latency_ms);
    } catch (e) { setError(e?.response?.data?.detail || "Triage failed"); }
    finally { setLoading(false); }
  };
  return (
    <Card title="Smart Maintenance Triage" subtitle="Describe the issue — the agent classifies severity, recommends a vendor, and drafts a tenant reply.">
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Issue description">
          <Textarea data-testid="triage-description" value={form.description}
            placeholder="e.g. Kitchen sink leak under cabinet — water pooling on floor. Started today."
            onChange={(e) => setForm({...form, description: e.target.value})}/>
        </Field>
        <div className="space-y-3">
          <Field label="Property / unit">
            <Input data-testid="triage-property" value={form.property_label}
              placeholder="e.g. 123 King St — Unit 2"
              onChange={(e) => setForm({...form, property_label: e.target.value})}/>
          </Field>
          <Field label="Tenant name">
            <Input data-testid="triage-tenant" value={form.tenant_name}
              placeholder="e.g. Alex Chen"
              onChange={(e) => setForm({...form, tenant_name: e.target.value})}/>
          </Field>
          <Field label="Reported via">
            <Select data-testid="triage-channel" value={form.reported_via}
              onChange={(e) => setForm({...form, reported_via: e.target.value})}>
              <option value="web">Web form</option>
              <option value="sms">SMS</option>
              <option value="voice">Voice call</option>
            </Select>
          </Field>
        </div>
      </div>
      <Button onClick={run} disabled={loading} data-testid="triage-run" className="mt-5">
        {loading ? <Loader/> : <>Run smart triage <ArrowRight className="w-4 h-4"/></>}
      </Button>
      <ResultPanel result={result} error={error} latencyMs={latency}/>
    </Card>
  );
}

/* -------------------- Feature: Multi-Agent Orchestration -------------------- */
function AgentsOrchestration() {
  const [form, setForm] = useState({
    scenario: "",
    property_label: "",
    portfolio_size: 1,
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [latency, setLatency] = useState(null);
  const [error, setError] = useState(null);
  const run = async () => {
    setLoading(true); setResult(null); setError(null); setLatency(null);
    try {
      const { data } = await api.post("/smart/agents/run", form);
      setResult(data.data); setLatency(data.latency_ms);
    } catch (e) { setError(e?.response?.data?.detail || "Run failed"); }
    finally { setLoading(false); }
  };
  return (
    <Card title="Smart Multi-Agent Orchestration" subtitle="Describe a scenario. Specialist agents (leasing, maintenance, unit prep, marketing, compliance, finance, comms) produce a coordinated plan.">
      <Field label="Scenario">
        <Textarea data-testid="agents-scenario" rows={4} value={form.scenario}
          placeholder="e.g. Unit going vacant next month. Tenant gave notice. I want to re-rent at market rate within 14 days."
          onChange={(e) => setForm({...form, scenario: e.target.value})}/>
      </Field>
      <div className="grid md:grid-cols-2 gap-3 mt-3">
        <Field label="Property">
          <Input data-testid="agents-property" value={form.property_label}
            placeholder="Property or unit label"
            onChange={(e) => setForm({...form, property_label: e.target.value})}/>
        </Field>
        <Field label="Portfolio size (units)">
          <Input data-testid="agents-portfolio" type="number" min={1} value={form.portfolio_size}
            onChange={(e) => setForm({...form, portfolio_size: Number(e.target.value)})}/>
        </Field>
      </div>
      <Button onClick={run} disabled={loading} data-testid="agents-run" className="mt-5">
        {loading ? <Loader/> : <>Run agent orchestration <ArrowRight className="w-4 h-4"/></>}
      </Button>
      <ResultPanel result={result} error={error} latencyMs={latency}/>
    </Card>
  );
}

/* -------------------- Feature: Conversational Ledger -------------------- */
function ConversationalLedger() {
  const [question, setQuestion] = useState("");
  const [snapshot, setSnapshot] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [latency, setLatency] = useState(null);
  const [error, setError] = useState(null);
  const run = async () => {
    setLoading(true); setResult(null); setError(null); setLatency(null);
    try {
      let ledger_snapshot = null;
      if (snapshot.trim()) {
        try { ledger_snapshot = JSON.parse(snapshot); }
        catch { toast.error("Ledger snapshot must be valid JSON — using none"); }
      }
      const { data } = await api.post("/smart/ledger/query", { question, ledger_snapshot });
      setResult(data.data); setLatency(data.latency_ms);
    } catch (e) { setError(e?.response?.data?.detail || "Query failed"); }
    finally { setLoading(false); }
  };
  return (
    <Card title="Smart Conversational Ledger" subtitle="Ask plain-English questions of your financial ledger. Optionally paste a JSON snapshot of recent transactions.">
      <Field label="Your question">
        <Textarea data-testid="ledger-question" value={question}
          placeholder='e.g. Which property had the highest net cashflow last quarter?'
          onChange={(e) => setQuestion(e.target.value)}/>
      </Field>
      <Field label="Ledger snapshot (optional JSON)" hint="Paste recent transactions / balances for grounded answers — leave blank for general guidance.">
        <Textarea data-testid="ledger-snapshot" rows={4} placeholder='{"properties":[{"name":"123 King","net_q":12400},...]}'
          value={snapshot} onChange={(e) => setSnapshot(e.target.value)}/>
      </Field>
      <Button onClick={run} disabled={loading} data-testid="ledger-run" className="mt-5">
        {loading ? <Loader/> : <>Ask the ledger <ArrowRight className="w-4 h-4"/></>}
      </Button>
      <ResultPanel result={result} error={error} latencyMs={latency}/>
    </Card>
  );
}

/* -------------------- Feature: Bookkeeping & Taxes -------------------- */
function BooksTaxes() {
  const [form, setForm] = useState({
    question: "",
    tax_year: new Date().getFullYear(),
    province: "ON",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [latency, setLatency] = useState(null);
  const [error, setError] = useState(null);
  const run = async () => {
    setLoading(true); setResult(null); setError(null); setLatency(null);
    try {
      const { data } = await api.post("/smart/books/query", form);
      setResult(data.data); setLatency(data.latency_ms);
    } catch (e) { setError(e?.response?.data?.detail || "Query failed"); }
    finally { setLoading(false); }
  };
  return (
    <Card title="Smart Bookkeeping & Taxes" subtitle="Canadian-tax-aware Q&A: T776 line items, NR4, CCA classes, GST/HST self-assessment, principal-residence exemption.">
      <Field label="Your tax / bookkeeping question">
        <Textarea data-testid="books-question" rows={4} value={form.question}
          placeholder="e.g. I bought a $1,400 fridge for a rental unit. Current expense or CCA Class 8?"
          onChange={(e) => setForm({...form, question: e.target.value})}/>
      </Field>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <Field label="Tax year">
          <Input data-testid="books-year" type="number" min={2015} max={2030} value={form.tax_year}
            onChange={(e) => setForm({...form, tax_year: Number(e.target.value)})}/>
        </Field>
        <Field label="Province">
          <Select data-testid="books-province" value={form.province}
            onChange={(e) => setForm({...form, province: e.target.value})}>
            {["AB","BC","MB","NB","NL","NS","ON","PE","QC","SK","NT","NU","YT"].map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </Select>
        </Field>
      </div>
      <Button onClick={run} disabled={loading} data-testid="books-run" className="mt-5">
        {loading ? <Loader/> : <>Ask the tax agent <ArrowRight className="w-4 h-4"/></>}
      </Button>
      <ResultPanel result={result} error={error} latencyMs={latency}/>
    </Card>
  );
}

/* -------------------- Feature: Visual Condition Assessment -------------------- */
function VisualAssessment() {
  const [form, setForm] = useState({
    image_urls: "",
    room_label: "",
    property_label: "",
    assessment_purpose: "condition",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [latency, setLatency] = useState(null);
  const [error, setError] = useState(null);
  const run = async () => {
    setLoading(true); setResult(null); setError(null); setLatency(null);
    try {
      const urls = form.image_urls.split(/\s|,/).map(s => s.trim()).filter(Boolean);
      if (urls.length === 0) { setError("Please supply at least one image URL"); setLoading(false); return; }
      const { data } = await api.post("/smart/visual/assess", { ...form, image_urls: urls });
      setResult(data.data); setLatency(data.latency_ms);
    } catch (e) { setError(e?.response?.data?.detail || "Assessment failed"); }
    finally { setLoading(false); }
  };
  return (
    <Card title="Smart Visual Condition Assessment"
      subtitle="Paste image URLs taken by the tenant or inspector. Vision model returns category-by-category condition scores, detected issues, and cost ranges.">
      <Field label="Photo URLs (one per line, max 6)">
        <Textarea data-testid="visual-urls" rows={3} value={form.image_urls}
          placeholder="Paste photo URLs — one per line"
          onChange={(e) => setForm({...form, image_urls: e.target.value})}/>
      </Field>
      <div className="grid md:grid-cols-3 gap-3 mt-3">
        <Field label="Room"><Input data-testid="visual-room" value={form.room_label}
          placeholder="e.g. Living room"
          onChange={(e) => setForm({...form, room_label: e.target.value})}/></Field>
        <Field label="Property"><Input data-testid="visual-property" value={form.property_label}
          placeholder="Property / unit"
          onChange={(e) => setForm({...form, property_label: e.target.value})}/></Field>
        <Field label="Purpose">
          <Select data-testid="visual-purpose" value={form.assessment_purpose}
            onChange={(e) => setForm({...form, assessment_purpose: e.target.value})}>
            <option value="condition">General condition</option>
            <option value="move_in">Move-in inspection</option>
            <option value="move_out">Move-out inspection</option>
            <option value="maintenance">Maintenance request</option>
          </Select>
        </Field>
      </div>
      <Button onClick={run} disabled={loading} data-testid="visual-run" className="mt-5">
        {loading ? <Loader/> : <>Run visual assessment <ArrowRight className="w-4 h-4"/></>}
      </Button>
      <ResultPanel result={result} error={error} latencyMs={latency}/>
    </Card>
  );
}

/* -------------------- Feature: Retention Risk -------------------- */
function RetentionRisk() {
  // Lease-row "Risk check" prefill: read once and remove.
  const leasePrefill = (() => {
    try {
      const raw = sessionStorage.getItem("crc_smart_retention_lease");
      if (!raw) return null;
      sessionStorage.removeItem("crc_smart_retention_lease");
      const p = JSON.parse(raw);
      // Derive lease months remaining from end_date when possible.
      let months_remaining = 0;
      if (p.end_date) {
        const ms = new Date(p.end_date).getTime() - Date.now();
        if (ms > 0) months_remaining = Math.max(0, Math.round(ms / (1000*60*60*24*30)));
      }
      return {
        tenant_name: p.tenant_email || "",
        current_rent_cad: Number(p.monthly_rent_cad) || 0,
        lease_months_remaining: months_remaining,
      };
    } catch { return null; }
  })();
  const [form, setForm] = useState({
    tenant_name: leasePrefill?.tenant_name || "",
    lease_months_remaining: leasePrefill?.lease_months_remaining ?? 0,
    months_in_unit: 0,
    current_rent_cad: leasePrefill?.current_rent_cad ?? 0,
    market_rent_cad: 0,
    maintenance_tickets_last_12mo: 0,
    open_tickets: 0,
    avg_message_response_hours: 0,
    late_payments_last_12mo: 0,
    last_communication_tone: "neutral",
    has_pets: false,
    has_partner: false,
    local_vacancy_rate_pct: 0,
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [latency, setLatency] = useState(null);
  const [error, setError] = useState(null);
  const run = async () => {
    setLoading(true); setResult(null); setError(null); setLatency(null);
    try {
      const { data } = await api.post("/smart/retention/predict", form);
      setResult(data.data); setLatency(data.latency_ms);
    } catch (e) { setError(e?.response?.data?.detail || "Prediction failed"); }
    finally { setLoading(false); }
  };
  const num = (k) => (e) => setForm({...form, [k]: Number(e.target.value)});
  const str = (k) => (e) => setForm({...form, [k]: e.target.value});
  const bool = (k) => (e) => setForm({...form, [k]: e.target.checked});
  return (
    <Card title="Smart Move-Out & Renewal Risk" subtitle="Calibrated retention-risk score (0-100) per tenant — drivers, recommended retention actions, and a suggested renewal offer.">
      <div className="grid md:grid-cols-3 gap-3">
        <Field label="Tenant name"><Input data-testid="risk-name" value={form.tenant_name} onChange={str("tenant_name")}/></Field>
        <Field label="Lease months remaining"><Input data-testid="risk-months-remaining" type="number" min={0} value={form.lease_months_remaining} onChange={num("lease_months_remaining")}/></Field>
        <Field label="Months in unit"><Input data-testid="risk-months-in" type="number" min={0} value={form.months_in_unit} onChange={num("months_in_unit")}/></Field>
        <Field label="Current rent (CAD)"><Input data-testid="risk-current-rent" type="number" min={0} value={form.current_rent_cad} onChange={num("current_rent_cad")}/></Field>
        <Field label="Market rent (CAD)"><Input data-testid="risk-market-rent" type="number" min={0} value={form.market_rent_cad} onChange={num("market_rent_cad")}/></Field>
        <Field label="Tickets last 12mo"><Input data-testid="risk-tickets" type="number" min={0} value={form.maintenance_tickets_last_12mo} onChange={num("maintenance_tickets_last_12mo")}/></Field>
        <Field label="Open tickets"><Input data-testid="risk-open" type="number" min={0} value={form.open_tickets} onChange={num("open_tickets")}/></Field>
        <Field label="Avg reply (hrs)"><Input type="number" min={0} step="0.5" value={form.avg_message_response_hours} onChange={num("avg_message_response_hours")}/></Field>
        <Field label="Late payments (12mo)"><Input data-testid="risk-late" type="number" min={0} value={form.late_payments_last_12mo} onChange={num("late_payments_last_12mo")}/></Field>
        <Field label="Last comms tone">
          <Select value={form.last_communication_tone} onChange={str("last_communication_tone")}>
            {["positive","neutral","frustrated","hostile"].map(t => <option key={t} value={t}>{t}</option>)}
          </Select>
        </Field>
        <Field label="Local vacancy (%)"><Input type="number" step="0.1" value={form.local_vacancy_rate_pct} onChange={num("local_vacancy_rate_pct")}/></Field>
        <div className="flex items-center gap-4 mt-7">
          <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={form.has_pets} onChange={bool("has_pets")}/> Pets</label>
          <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={form.has_partner} onChange={bool("has_partner")}/> Partner</label>
        </div>
      </div>
      <Button onClick={run} disabled={loading} data-testid="risk-run" className="mt-5">
        {loading ? <Loader/> : <>Score retention risk <ArrowRight className="w-4 h-4"/></>}
      </Button>
      <ResultPanel result={result} error={error} latencyMs={latency}/>
    </Card>
  );
}

/* -------------------- Feature: Anomaly Detection -------------------- */
const SAMPLE_TX_PLACEHOLDER = `[
  {"id":"t1","date":"2026-01-03","vendor":"ABC Plumbing","amount":380,"category":"plumbing","property":"123 King"},
  {"id":"t2","date":"2026-01-05","vendor":"ABC Plumbing","amount":380,"category":"plumbing","property":"123 King"}
]`;
function AnomalyDetect() {
  const [txText, setTxText] = useState("");
  const [baseline, setBaseline] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [latency, setLatency] = useState(null);
  const [error, setError] = useState(null);
  const run = async () => {
    setLoading(true); setResult(null); setError(null); setLatency(null);
    try {
      let transactions = []; let historical_monthly_avg = null;
      try { transactions = JSON.parse(txText); }
      catch { setError("Transactions JSON is invalid"); setLoading(false); return; }
      try { historical_monthly_avg = baseline ? JSON.parse(baseline) : null; } catch { /* ok */ }
      const { data } = await api.post("/smart/anomaly/detect", {
        transactions, historical_monthly_avg, months_of_history: 12, portfolio_size_units: 8,
      });
      setResult(data.data); setLatency(data.latency_ms);
    } catch (e) { setError(e?.response?.data?.detail || "Detection failed"); }
    finally { setLoading(false); }
  };
  return (
    <Card title="Smart Anomaly & Expense Detection" subtitle="Flags duplicates, utility spikes, vendor overcharging, and misclassified capex.">
      <Field label="Recent transactions (JSON array)">
        <Textarea data-testid="anomaly-tx" rows={6} value={txText}
          placeholder={SAMPLE_TX_PLACEHOLDER}
          onChange={(e) => setTxText(e.target.value)}/>
      </Field>
      <Field label="Historical monthly average by category (JSON, optional)" hint="Lets the model spot >50% spikes vs. your baseline.">
        <Textarea data-testid="anomaly-baseline" rows={2} value={baseline}
          placeholder='{"plumbing": 200, "utilities-electric": 240}'
          onChange={(e) => setBaseline(e.target.value)}/>
      </Field>
      <Button onClick={run} disabled={loading} data-testid="anomaly-run" className="mt-5">
        {loading ? <Loader/> : <>Scan for anomalies <ArrowRight className="w-4 h-4"/></>}
      </Button>
      <ResultPanel result={result} error={error} latencyMs={latency}/>
    </Card>
  );
}

/* -------------------- Feature: Portfolio Benchmarking -------------------- */
function PortfolioBenchmark() {
  const [form, setForm] = useState({
    region: "",
    portfolio_size_units: 1,
    avg_turnover_days: 0,
    delinquency_rate_pct: 0,
    maintenance_cost_per_unit_cad: 0,
    occupancy_rate_pct: 0,
    avg_rent_cad: 0,
    eviction_rate_pct: 0,
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [latency, setLatency] = useState(null);
  const [error, setError] = useState(null);
  const run = async () => {
    setLoading(true); setResult(null); setError(null); setLatency(null);
    try {
      const { data } = await api.post("/smart/benchmark/compare", form);
      setResult(data.data); setLatency(data.latency_ms);
    } catch (e) { setError(e?.response?.data?.detail || "Benchmark failed"); }
    finally { setLoading(false); }
  };
  const num = (k) => (e) => setForm({...form, [k]: Number(e.target.value)});
  return (
    <Card title="Smart Portfolio Benchmarking" subtitle="Compare your KPIs against regional medians + top quartiles using CMHC / CRA / Altus reference data.">
      <div className="grid md:grid-cols-3 gap-3">
        <Field label="Region"><Input data-testid="bench-region" value={form.region}
          placeholder="e.g. Toronto, ON"
          onChange={(e) => setForm({...form, region: e.target.value})}/></Field>
        <Field label="Portfolio size (units)"><Input data-testid="bench-portfolio" type="number" min={1} value={form.portfolio_size_units} onChange={num("portfolio_size_units")}/></Field>
        <Field label="Avg turnover (days)"><Input type="number" min={0} value={form.avg_turnover_days} onChange={num("avg_turnover_days")}/></Field>
        <Field label="Delinquency rate (%)"><Input type="number" step="0.1" min={0} value={form.delinquency_rate_pct} onChange={num("delinquency_rate_pct")}/></Field>
        <Field label="Maintenance cost / unit (CAD)"><Input type="number" min={0} value={form.maintenance_cost_per_unit_cad} onChange={num("maintenance_cost_per_unit_cad")}/></Field>
        <Field label="Occupancy (%)"><Input type="number" step="0.1" min={0} max={100} value={form.occupancy_rate_pct} onChange={num("occupancy_rate_pct")}/></Field>
        <Field label="Avg rent (CAD)"><Input type="number" min={0} value={form.avg_rent_cad} onChange={num("avg_rent_cad")}/></Field>
        <Field label="Eviction rate (%)"><Input type="number" step="0.1" min={0} value={form.eviction_rate_pct} onChange={num("eviction_rate_pct")}/></Field>
      </div>
      <Button onClick={run} disabled={loading} data-testid="bench-run" className="mt-5">
        {loading ? <Loader/> : <>Compare to benchmarks <ArrowRight className="w-4 h-4"/></>}
      </Button>
      <ResultPanel result={result} error={error} latencyMs={latency}/>
    </Card>
  );
}

/* -------------------- Feature: Brokerage Split Payments -------------------- */
function BrokerageSplit() {
  const [cfg, setCfg] = useState({
    property_id: "", property_label: "",
    owner_share_pct: 90, brokerage_share_pct: 10,
    owner_stripe_account: "", brokerage_stripe_account: "",
  });
  const [calc, setCalc] = useState({ property_id: "", rent_cad: 0 });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [latency, setLatency] = useState(null);
  const [error, setError] = useState(null);
  const num = (k, src, setter) => (e) => setter({ ...src, [k]: Number(e.target.value) });
  const str = (k, src, setter) => (e) => setter({ ...src, [k]: e.target.value });

  const saveCfg = async () => {
    setLoading(true); setError(null);
    try {
      await api.post("/smart/brokerage/config", cfg);
      toast.success("Split config saved");
    } catch (e) { setError(e?.response?.data?.detail || "Could not save config"); }
    finally { setLoading(false); }
  };
  const run = async () => {
    setLoading(true); setResult(null); setError(null); setLatency(null);
    const t0 = Date.now();
    try {
      const { data } = await api.post("/smart/brokerage/split", calc);
      setResult(data); setLatency(Date.now() - t0);
    } catch (e) { setError(e?.response?.data?.detail || "Calc failed"); }
    finally { setLoading(false); }
  };

  return (
    <Card title="Smart Brokerage Split Payments"
      subtitle="Auto-routes a rent transaction (e.g., 90% to owner / 10% to brokerage) using Stripe Connect destination charges — no trust account required.">
      <div className="grid md:grid-cols-2 gap-5">
        <div className="space-y-3">
          <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500">1. Split configuration</div>
          <Field label="Property ID"><Input data-testid="brk-prop-id" value={cfg.property_id} placeholder="e.g. p1" onChange={str("property_id", cfg, setCfg)}/></Field>
          <Field label="Property label"><Input value={cfg.property_label} placeholder="Property / unit" onChange={str("property_label", cfg, setCfg)}/></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Owner %"><Input data-testid="brk-owner-pct" type="number" min={0} max={100} value={cfg.owner_share_pct} onChange={num("owner_share_pct", cfg, setCfg)}/></Field>
            <Field label="Brokerage %"><Input data-testid="brk-brk-pct" type="number" min={0} max={100} value={cfg.brokerage_share_pct} onChange={num("brokerage_share_pct", cfg, setCfg)}/></Field>
          </div>
          <Field label="Owner Stripe acct"><Input data-testid="brk-owner-acct" value={cfg.owner_stripe_account} placeholder="acct_…" onChange={str("owner_stripe_account", cfg, setCfg)}/></Field>
          <Field label="Brokerage Stripe acct"><Input data-testid="brk-brk-acct" value={cfg.brokerage_stripe_account} placeholder="acct_…" onChange={str("brokerage_stripe_account", cfg, setCfg)}/></Field>
          <Button onClick={saveCfg} disabled={loading} data-testid="brk-save-cfg">Save split config</Button>
        </div>
        <div className="space-y-3">
          <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500">2. Simulate a rent transaction</div>
          <Field label="Property ID"><Input data-testid="brk-calc-prop" value={calc.property_id} onChange={str("property_id", calc, setCalc)}/></Field>
          <Field label="Rent (CAD)"><Input data-testid="brk-calc-rent" type="number" min={0} value={calc.rent_cad} onChange={num("rent_cad", calc, setCalc)}/></Field>
          <Button onClick={run} disabled={loading} data-testid="brk-run">
            {loading ? <Loader/> : <>Calculate split <ArrowRight className="w-4 h-4"/></>}
          </Button>
        </div>
      </div>
      <ResultPanel result={result} error={error} latencyMs={latency}/>
    </Card>
  );
}

/* -------------------- Feature: Smart-Lock Vendor Codes -------------------- */
function SmartLockCodes() {
  // Generate a starter work order ID inside useEffect to satisfy
  // react-hooks/purity (Math.random in useState initial is non-pure).
  const [form, setForm] = useState({
    work_order_id: "",
    vendor_name: "", vendor_phone: "",
    property_id: "", property_label: "", unit_label: "",
    valid_hours: 8,
  });
  const [issued, setIssued] = useState(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    setForm((f) => ({ ...f, work_order_id: `wo_${Math.random().toString(36).slice(2, 8)}` }));
  }, []);
  const str = (k) => (e) => setForm({...form, [k]: e.target.value});
  const num = (k) => (e) => setForm({...form, [k]: Number(e.target.value)});

  const refresh = async () => {
    try {
      const { data } = await api.get(`/smart/locks/list?property_id=${form.property_id}`);
      setList(data.codes || []);
    } catch { /* ignore */ }
  };
  const issue = async () => {
    setLoading(true); setError(null); setIssued(null);
    try {
      const { data } = await api.post("/smart/locks/issue-code", form);
      setIssued(data.code);
      toast.success(`PIN ${data.code.code} issued`);
      refresh();
    } catch (e) { setError(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  const revoke = async (workOrderId) => {
    try {
      await api.post("/smart/locks/revoke", { work_order_id: workOrderId, reason: "manual_revoke" });
      toast.success("PIN revoked");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Revoke failed"); }
  };
  useEffect(() => {
    // Initial load + then we rely on issue/revoke to refresh manually.
    (async () => {
      try {
        const { data } = await api.get(`/smart/locks/list?property_id=${form.property_id}`);
        setList(data.codes || []);
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card title="Smart Lock Vendor Verification"
      subtitle="Generates a time-restricted PIN bound to a work order. Auto-revokes the moment the WO is marked complete.">
      <div className="grid md:grid-cols-2 gap-5">
        <div className="space-y-3">
          <Field label="Work order ID"><Input data-testid="lock-wo-id" value={form.work_order_id} placeholder="Auto-generated on submit" onChange={str("work_order_id")}/></Field>
          <Field label="Vendor name"><Input data-testid="lock-vendor" value={form.vendor_name} placeholder="e.g. ABC Plumbing" onChange={str("vendor_name")}/></Field>
          <Field label="Vendor phone"><Input value={form.vendor_phone} placeholder="+1 …" onChange={str("vendor_phone")}/></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Property ID"><Input value={form.property_id} placeholder="Property ID" onChange={str("property_id")}/></Field>
            <Field label="Valid hours"><Input data-testid="lock-hours" type="number" min={1} max={168} value={form.valid_hours} onChange={num("valid_hours")}/></Field>
          </div>
          <Button onClick={issue} disabled={loading} data-testid="lock-issue">
            {loading ? <Loader/> : <>Issue vendor PIN <ArrowRight className="w-4 h-4"/></>}
          </Button>
          {error && <div className="text-xs text-red-700">{error}</div>}
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-2">Recent codes</div>
          <div className="space-y-2 max-h-[320px] overflow-y-auto" data-testid="lock-list">
            {list.length === 0 && <div className="text-sm text-slate-500">No codes yet.</div>}
            {list.map((c) => (
              <div key={c.id} className="p-3 rounded-xl border border-slate-200 bg-white text-sm flex items-center gap-3">
                <div className="font-mono text-lg font-bold tabular-nums text-slate-900">{c.code}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-800 truncate">{c.vendor_name}</div>
                  <div className="text-[11px] text-slate-500 truncate">WO {c.work_order_id} · until {new Date(c.valid_until).toLocaleString("en-CA")}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full
                  ${c.status === "active" ? "bg-emerald-100 text-emerald-700"
                    : c.status === "expired" ? "bg-slate-100 text-slate-500"
                    : "bg-amber-100 text-amber-700"}`}>{c.status}</span>
                {c.status === "active" && (
                  <button onClick={() => revoke(c.work_order_id)} data-testid="lock-revoke"
                    className="text-[11px] text-red-600 hover:text-red-800 font-semibold">Revoke</button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      {issued && (
        <div className="mt-5 p-4 rounded-xl bg-emerald-50 border border-emerald-200" data-testid="lock-issued">
          <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-emerald-700">Latest PIN</div>
          <div className="mt-1 font-mono text-3xl font-extrabold tabular-nums text-slate-900">{issued.code}</div>
          <div className="text-xs text-slate-600 mt-1">Valid {new Date(issued.valid_from).toLocaleString("en-CA")} → {new Date(issued.valid_until).toLocaleString("en-CA")}</div>
        </div>
      )}
    </Card>
  );
}

/* -------------------- Feature: Smart Leasing Automation -------------------- */
function LeasingAutomation() {
  // Pull a property prefill from sessionStorage (set by /properties → "Distribute")
  // so we land on the leasing tab with the right listing context.
  const prefill = (() => {
    try {
      const raw = sessionStorage.getItem("crc_smart_leasing_property");
      if (!raw) return null;
      sessionStorage.removeItem("crc_smart_leasing_property");
      const p = JSON.parse(raw);
      return { property_id: p.id || "p1", property_label: p.name || "", address: p.address || "" };
    } catch { return null; }
  })();

  const [listing, setListing] = useState({
    property_id: prefill?.property_id || "",
    property_label: prefill?.property_label || "",
    address: prefill?.address || "",
    bedrooms: 1, bathrooms: 1, sqft: 0, monthly_rent_cad: 0,
    available_from: "", amenities: [],
    description: "",
    // Photos collected via the Smart Listing Photo Booth — starts empty so
    // the landlord must upload real photos (no demo render image).
    photo_urls: [],
    pre_qualification_min_income_multiple: 2.5,
    pre_qualification_min_credit: 620,
    accept_pets: true, accept_smokers: false,
  });
  const [uploading, setUploading] = useState(false);
  const [prequal, setPrequal] = useState({
    applicant_name: "", applicant_email: "",
    monthly_gross_income_cad: 0, credit_score: 0,
    pets: 0, smoker: false, employment_status: "employed", months_at_current_job: 0,
  });
  const [activeListing, setActiveListing] = useState(null);
  const [prequalRes, setPrequalRes] = useState(null);
  const [showing, setShowing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const num = (k, src, setter) => (e) => setter({...src, [k]: Number(e.target.value)});
  const str = (k, src, setter) => (e) => setter({...src, [k]: e.target.value});
  const bool = (k, src, setter) => (e) => setter({...src, [k]: e.target.checked});

  const distribute = async () => {
    setLoading(true); setError(null); setActiveListing(null); setPrequalRes(null); setShowing(null);
    try {
      const { data } = await api.post("/smart/leasing/distribute", listing);
      setActiveListing(data.listing);
      toast.success(`Listing distributed (${data.listing.distribution.length} channels)`);
    } catch (e) { setError(e?.response?.data?.detail || "Distribute failed"); }
    finally { setLoading(false); }
  };

  /**
   * One-click "Post everywhere": distribute + auto-open every paste
   * channel in its own tab + auto-copy the first channel's clipboard
   * payload. Placeholder tabs are opened SYNCHRONOUSLY before the API
   * call so the browser's popup blocker stays out of our way.
   */
  const postEverywhere = async () => {
    setLoading(true); setError(null); setActiveListing(null); setPrequalRes(null); setShowing(null);
    // Pre-open empty tabs inside the user-gesture context. After the
    // distribute call returns we redirect each placeholder to its paste
    // URL. Two placeholders covers Kijiji + FB Marketplace.
    const placeholders = [
      window.open("about:blank", "_blank", "noopener,noreferrer"),
      window.open("about:blank", "_blank", "noopener,noreferrer"),
    ];
    try {
      const { data } = await api.post("/smart/leasing/distribute", listing);
      setActiveListing(data.listing);
      const pasteChannels = (data.listing.distribution || []).filter(
        (c) => c.status === "ready_to_paste" && c.paste_url
      );
      pasteChannels.forEach((c, i) => {
        const ph = placeholders[i];
        if (ph && !ph.closed) ph.location = c.paste_url;
      });
      // Close any unused placeholders
      placeholders.slice(pasteChannels.length).forEach((ph) => ph && !ph.closed && ph.close());
      // Auto-copy the first channel's clipboard payload so the landlord
      // can paste immediately into the tab that just opened.
      if (pasteChannels[0]?.clipboard_text) {
        try {
          await navigator.clipboard.writeText(pasteChannels[0].clipboard_text);
          const firstName = pasteChannels[0].channel === "kijiji_paste" ? "Kijiji"
                          : pasteChannels[0].channel === "facebook_marketplace_paste" ? "FB Marketplace"
                          : pasteChannels[0].channel;
          toast.success(`Posted everywhere — ${firstName} text copied to clipboard. Paste into the open tab.`);
        } catch {
          toast.message(`Posted everywhere — open tabs ready. Use "Copy text" per channel to paste.`);
        }
      } else {
        toast.success(`Posted to ${data.listing.distribution.length} channels`);
      }
    } catch (e) {
      placeholders.forEach((ph) => ph && !ph.closed && ph.close());
      setError(e?.response?.data?.detail || "Post-everywhere failed");
    } finally { setLoading(false); }
  };
  const runPrequal = async () => {
    if (!activeListing) return toast.error("Publish a listing first");
    setLoading(true); setError(null);
    try {
      const { data } = await api.post("/smart/leasing/prequalify",
        { listing_id: activeListing.id, ...prequal });
      setPrequalRes(data.result);
      toast.success(`Decision: ${data.result.decision} (score ${data.result.score})`);
    } catch (e) { setError(e?.response?.data?.detail || "Prequal failed"); }
    finally { setLoading(false); }
  };
  const bookShowing = async () => {
    if (!activeListing || !prequalRes) return toast.error("Run pre-qualification first");
    setLoading(true); setError(null);
    try {
      const slot = new Date(Date.now() + 24*60*60*1000); slot.setHours(14, 0, 0, 0);
      const { data } = await api.post("/smart/leasing/book-showing", {
        listing_id: activeListing.id,
        applicant_name: prequal.applicant_name,
        applicant_email: prequal.applicant_email,
        slot_iso: slot.toISOString(),
        self_tour: true,
        prequal_decision: prequalRes.decision,
      });
      setShowing(data.showing);
      toast.success("Showing booked");
    } catch (e) { setError(e?.response?.data?.detail || "Booking failed"); }
    finally { setLoading(false); }
  };

  return (
    <Card title="Smart Leasing Automation"
      subtitle="One listing publishes to Kijiji + Facebook Marketplace (one-click deep-links), your Facebook Page, Zumper, and Rentals.ca. Pre-qualifies leads in <200ms and auto-books showings — including self-tour with a one-time smart-lock PIN.">

      <div className="mb-4 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
        <b>Use real photos only.</b> Listings auto-post to your live Facebook page — never use smart-generated or stock-render images. Use the Photo Booth below to capture real unit shots directly from your phone.
      </div>

      {/* Smart Listing Photo Booth */}
      <div className="mb-5 p-4 rounded-2xl border border-emerald-200 bg-emerald-50/40">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center flex-none">
            <Camera className="w-5 h-5"/>
          </div>
          <div className="flex-1">
            <div className="font-display text-base font-bold text-slate-900">Smart Listing Photo Booth</div>
            <p className="text-xs text-slate-600 mt-0.5">Snap photos directly from your phone — they upload to a CDN URL the listing channels can fetch.</p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <label
                data-testid="photo-booth-camera-trigger"
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold cursor-pointer transition-colors"
              >
                <Camera className="w-4 h-4"/> Take / upload photos
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  className="hidden"
                  data-testid="photo-booth-file-input"
                  onChange={async (e) => {
                    const files = Array.from(e.target.files || []);
                    if (!files.length) return;
                    setUploading(true);
                    const uploaded = [];
                    for (const f of files) {
                      const fd = new FormData();
                      fd.append("file", f);
                      try {
                        const { data } = await api.post("/smart/listings/photo-upload", fd, {
                          headers: { "Content-Type": "multipart/form-data" },
                        });
                        if (data?.url) uploaded.push(data.url);
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || `Couldn't upload ${f.name}`);
                      }
                    }
                    if (uploaded.length) {
                      setListing((prev) => ({ ...prev, photo_urls: [...prev.photo_urls, ...uploaded] }));
                      toast.success(`${uploaded.length} photo${uploaded.length > 1 ? "s" : ""} uploaded`);
                    }
                    setUploading(false);
                    e.target.value = "";
                  }}
                />
              </label>
              {uploading && <span className="text-xs text-slate-500 flex items-center gap-2"><Loader/>Uploading…</span>}
              <span className="text-xs text-slate-500" data-testid="photo-booth-count">
                {listing.photo_urls.length} photo{listing.photo_urls.length === 1 ? "" : "s"} attached
              </span>
            </div>
            {listing.photo_urls.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="photo-booth-thumbs">
                {listing.photo_urls.map((url, idx) => (
                  <div key={url} className="relative group">
                    <img src={url} alt={`Listing photo ${idx + 1}`}
                      className="w-20 h-20 rounded-lg object-cover border border-emerald-200"
                      loading="lazy"
                    />
                    <button type="button"
                      onClick={() => setListing((p) => ({ ...p, photo_urls: p.photo_urls.filter((u) => u !== url) }))}
                      data-testid={`photo-booth-remove-${idx}`}
                      aria-label="Remove photo"
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white border border-slate-300 text-slate-600 hover:text-red-600 hover:border-red-300 text-xs font-bold flex items-center justify-center shadow-sm transition-colors"
                    >×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Step 1 — Listing */}
      <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-emerald-700 mb-2">1. Publish listing</div>
      <div className="grid md:grid-cols-3 gap-3">
        <Field label="Address"><Input data-testid="lease-addr" value={listing.address} placeholder="123 Main St, Toronto, ON" onChange={str("address", listing, setListing)}/></Field>
        <Field label="Property ID"><Input value={listing.property_id} placeholder="Your internal ID" onChange={str("property_id", listing, setListing)}/></Field>
        <Field label="Monthly rent (CAD)"><Input data-testid="lease-rent" type="number" value={listing.monthly_rent_cad} onChange={num("monthly_rent_cad", listing, setListing)}/></Field>
        <Field label="Bedrooms"><Input type="number" min={0} max={10} value={listing.bedrooms} onChange={num("bedrooms", listing, setListing)}/></Field>
        <Field label="Bathrooms"><Input type="number" step="0.5" min={0} max={10} value={listing.bathrooms} onChange={num("bathrooms", listing, setListing)}/></Field>
        <Field label="Sqft"><Input type="number" value={listing.sqft} onChange={num("sqft", listing, setListing)}/></Field>
        <Field label="Min credit"><Input type="number" min={300} max={900} value={listing.pre_qualification_min_credit} onChange={num("pre_qualification_min_credit", listing, setListing)}/></Field>
        <Field label="Min income (× rent)"><Input type="number" step="0.1" min={1} max={5} value={listing.pre_qualification_min_income_multiple} onChange={num("pre_qualification_min_income_multiple", listing, setListing)}/></Field>
        <div className="flex items-center gap-4 mt-7">
          <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={listing.accept_pets} onChange={bool("accept_pets", listing, setListing)}/> Pets</label>
          <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={listing.accept_smokers} onChange={bool("accept_smokers", listing, setListing)}/> Smokers</label>
        </div>
      </div>
      <Button onClick={distribute} disabled={loading} data-testid="lease-distribute" className="mt-4">
        {loading ? <Loader/> : <>Distribute listing <ArrowRight className="w-4 h-4"/></>}
      </Button>
      <button
        type="button"
        onClick={postEverywhere}
        disabled={loading}
        data-testid="lease-post-everywhere"
        className="mt-4 ml-2 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-sky-600 to-emerald-600 hover:from-sky-700 hover:to-emerald-700 text-white text-sm font-bold shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading ? <Loader/> : <><Megaphone className="w-4 h-4"/> Post everywhere</>}
      </button>

      {activeListing && (
        <div className="mt-5 p-4 rounded-xl bg-emerald-50/60 border border-emerald-200" data-testid="lease-distribution">
          <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-emerald-700">Distribution ledger</div>
          <div className="mt-2 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {activeListing.distribution.map((d) => {
              // Paste channels (Kijiji, FB Marketplace): show two buttons —
              // "Copy text" (clipboard only) + "Post to X →" (copy + open).
              // The "Post everywhere" button at the top opens all paste tabs
              // in one go inside the user-gesture context.
              const isPaste = d.status === "ready_to_paste" && d.paste_url;
              const channelLabel =
                d.channel === "kijiji_paste" ? "Kijiji"
                : d.channel === "facebook_marketplace_paste" ? "FB Marketplace"
                : d.channel;
              const onPasteClick = async () => {
                try {
                  if (d.clipboard_text) await navigator.clipboard.writeText(d.clipboard_text);
                  toast.success(`${channelLabel} text copied — paste into the open tab`);
                } catch {
                  toast.message(`Open ${channelLabel} and paste manually`);
                }
                window.open(d.paste_url, "_blank", "noopener,noreferrer");
              };
              const onCopyOnly = async (e) => {
                e.stopPropagation();
                try {
                  if (d.clipboard_text) {
                    await navigator.clipboard.writeText(d.clipboard_text);
                    toast.success(`${channelLabel} text copied to clipboard`);
                  }
                } catch {
                  toast.error("Couldn't copy — long-press to copy manually");
                }
              };
              return (
                <div key={d.channel} className="text-xs bg-white border border-slate-200 rounded-lg p-2.5 flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full flex-none
                    ${d.status === "published" ? "bg-emerald-500"
                      : d.status === "ready_to_paste" ? "bg-sky-500"
                      : "bg-amber-400"}`}/>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-800 truncate">{channelLabel}</div>
                    <div className="text-[10px] text-slate-500 truncate">{d.note}</div>
                  </div>
                  {isPaste ? (
                    <>
                      <button type="button" onClick={onCopyOnly}
                        data-testid={`lease-${d.channel}-copy-btn`}
                        title="Copy listing text only"
                        className="text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50 transition-colors whitespace-nowrap">
                        Copy
                      </button>
                      <button type="button" onClick={onPasteClick}
                        data-testid={`lease-${d.channel}-post-btn`}
                        className="text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded-md bg-sky-600 text-white hover:bg-sky-700 transition-colors whitespace-nowrap">
                        Post →
                      </button>
                    </>
                  ) : (
                    <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">{d.status}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Step 2 — Pre-qualify */}
      {activeListing && (
        <>
          <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-emerald-700 mt-6 mb-2">2. Pre-qualify a lead</div>
          <div className="grid md:grid-cols-3 gap-3">
            <Field label="Applicant"><Input value={prequal.applicant_name} placeholder="Applicant name" onChange={str("applicant_name", prequal, setPrequal)}/></Field>
            <Field label="Email"><Input value={prequal.applicant_email} placeholder="applicant@example.com" onChange={str("applicant_email", prequal, setPrequal)}/></Field>
            <Field label="Gross income/mo (CAD)"><Input data-testid="lease-income" type="number" min={0} value={prequal.monthly_gross_income_cad} onChange={num("monthly_gross_income_cad", prequal, setPrequal)}/></Field>
            <Field label="Credit score"><Input data-testid="lease-credit" type="number" min={300} max={900} value={prequal.credit_score} onChange={num("credit_score", prequal, setPrequal)}/></Field>
            <Field label="Pets"><Input type="number" min={0} value={prequal.pets} onChange={num("pets", prequal, setPrequal)}/></Field>
            <Field label="Employment">
              <Select value={prequal.employment_status} onChange={str("employment_status", prequal, setPrequal)}>
                {["employed","self_employed","student","retired","unemployed"].map(o => <option key={o} value={o}>{o}</option>)}
              </Select>
            </Field>
          </div>
          <Button onClick={runPrequal} disabled={loading} data-testid="lease-prequal" className="mt-4">
            {loading ? <Loader/> : <>Run pre-qualification <ArrowRight className="w-4 h-4"/></>}
          </Button>
        </>
      )}

      {prequalRes && (
        <div className="mt-5 p-4 rounded-xl border border-slate-200 bg-white" data-testid="lease-prequal-result">
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-xs uppercase tracking-wider font-bold
              ${prequalRes.decision === "approved" ? "bg-emerald-100 text-emerald-700"
                : prequalRes.decision === "conditional" ? "bg-amber-100 text-amber-700"
                : "bg-red-100 text-red-700"}`}>{prequalRes.decision}</span>
            <div className="text-sm">Score: <b>{prequalRes.score}/100</b></div>
          </div>
          <ul className="mt-3 space-y-1.5 text-xs text-slate-700">
            {prequalRes.reasons.map((r, i) => (
              <li key={i} className="flex gap-2">
                <span className={r.passed ? "text-emerald-600" : "text-red-600"}>{r.passed ? "✓" : "✗"}</span>
                <span><b>{r.rule}:</b> {r.detail}</span>
              </li>
            ))}
          </ul>
          <Button onClick={bookShowing} disabled={loading || prequalRes.decision === "denied"} data-testid="lease-book" className="mt-4">
            {loading ? <Loader/> : <>Book self-tour showing <ArrowRight className="w-4 h-4"/></>}
          </Button>
        </div>
      )}

      {showing && (
        <div className="mt-4 p-4 rounded-xl bg-emerald-50 border border-emerald-200" data-testid="lease-showing">
          <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-emerald-700">Showing booked</div>
          <div className="mt-1 text-sm font-semibold text-slate-900">{new Date(showing.slot_at).toLocaleString("en-CA")}</div>
          {showing.self_tour_pin && (
            <div className="mt-2 text-xs text-slate-700">
              Self-tour PIN: <span className="font-mono font-bold text-base text-slate-900">{showing.self_tour_pin}</span>
            </div>
          )}
        </div>
      )}

      {error && <div className="mt-4 text-sm text-red-700">{error}</div>}
    </Card>
  );
}

/* ============================== Hub page ============================== */
export default function SmartFeatures() {
  // Accept ?tab=<id> deep-link so other pages (Tickets, Properties, Leases)
  // can drop the user straight into the relevant Smart feature.
  const initialTab = (() => {
    try {
      const t = new URLSearchParams(window.location.search).get("tab");
      return FEATURES.find((f) => f.id === t) ? t : FEATURES[0].id;
    } catch { return FEATURES[0].id; }
  })();
  const [active, setActive] = useState(initialTab);

  return (
    <div className="space-y-7" data-testid="smart-features-page">
      <PageHeader
        icon={Sparkles}
        title="Smart features"
        subtitle="Eleven smart features that run in under 10 seconds and return structured output your team can act on. Built for Canadian landlords."
      />
      <div className="-mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700">
        <CheckCircle2 className="w-3 h-3"/> Active subscription required · Paid subscription required — cancel anytime
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {FEATURES.map((f) => (
          <SmartCard key={f.id} {...f} active={active === f.id} onClick={() => setActive(f.id)}/>
        ))}
      </div>

      <div data-testid={`smart-active-${active}`}>
        {active === "maintenance" && <MaintenanceTriage/>}
        {active === "agents"      && <AgentsOrchestration/>}
        {active === "ledger"      && <ConversationalLedger/>}
        {active === "books"       && <BooksTaxes/>}
        {active === "visual"      && <VisualAssessment/>}
        {active === "retention"   && <RetentionRisk/>}
        {active === "anomaly"     && <AnomalyDetect/>}
        {active === "benchmark"   && <PortfolioBenchmark/>}
        {active === "brokerage"   && <BrokerageSplit/>}
        {active === "locks"       && <SmartLockCodes/>}
        {active === "leasing"     && <LeasingAutomation/>}
      </div>
    </div>
  );
}
